/**
 * Controls main menu hover interactions with the slide-out megamenu panel.
 * Visibility is driven by class names: `body.megamenu-active`, `.megamenu.active`,
 * and `[data-megamenu-link].active`.
 */
function initMegamenu(header) {
  const menu = header.querySelector('.main-menu');
  const megamenuWrapper = header.querySelector('.megamenu-wrapper');

  if (!menu || !megamenuWrapper) return;

  /** @type {HTMLAnchorElement | null} */
  let activeLink = null;

  /**
   * The megamenu panel is `position: fixed` and sits in a gap below the header
   * bar, so moving the pointer from a menu link toward the panel briefly exits
   * the header (crossing the page overlay). Closing on that `mouseleave`
   * immediately makes the panel slide out before it can be reached. Instead we
   * arm a short grace timer on leave and cancel it if the pointer re-enters the
   * header, menu, or panel.
   */
  const CLOSE_DELAY = 250;
  /** @type {number | null} */
  let closeTimer = null;

  function cancelClose() {
    if (closeTimer !== null) {
      clearTimeout(closeTimer);
      closeTimer = null;
    }
  }

  function scheduleClose() {
    cancelClose();
    closeTimer = window.setTimeout(() => {
      closeTimer = null;
      closeMegamenu();
    }, CLOSE_DELAY);
  }

  function clearActiveStates() {
    // Clear active links in both the desktop menu and the mobile menu list.
    document.querySelectorAll('[data-megamenu-link].active').forEach((element) => {
      element.classList.remove('active');
    });

    megamenuWrapper.querySelectorAll('.megamenu.active').forEach((element) => {
      element.classList.remove('active');
    });
  }

  function closeMegamenu() {
    document.body.classList.remove('megamenu-active');
  }

  function getDefaultMenuLink() {
    if (activeLink instanceof HTMLAnchorElement) {
      const megamenuId = activeLink.dataset.megamenuLink;
      if (megamenuId && megamenuWrapper.querySelector(`[data-megamenu-id="${megamenuId}"]`)) {
        return activeLink;
      }
    }

    const links = menu.querySelectorAll('[data-megamenu-link]');
    for (const link of links) {
      if (!(link instanceof HTMLAnchorElement)) continue;

      const megamenuId = link.dataset.megamenuLink;
      if (megamenuId && megamenuWrapper.querySelector(`[data-megamenu-id="${megamenuId}"]`)) {
        return link;
      }
    }

    return null;
  }

  /**
   * @param {HTMLAnchorElement} link
   */
  function activate(link) {
    const megamenuId = link.dataset.megamenuLink;
    if (!megamenuId) return;

    const panel = megamenuWrapper.querySelector(`[data-megamenu-id="${megamenuId}"]`);
    if (!(panel instanceof HTMLElement)) return;

    if (activeLink === link) {
      if (!document.body.classList.contains('megamenu-active')) {
        document.body.classList.add('megamenu-active');
      }
      return;
    }

    clearActiveStates();

    document.body.classList.add('megamenu-active');
    link.classList.add('active');
    panel.classList.add('active');
    activeLink = link;
  }

  /**
   * @param {MouseEvent} event
   */
  function handleMenuOver(event) {
    cancelClose();

    const link = event.target instanceof Element ? event.target.closest('[data-megamenu-link]') : null;
    if (!(link instanceof HTMLAnchorElement) || !menu.contains(link)) return;

    activate(link);
  }

  /**
   * @param {MouseEvent} event
   */
  function handleMegamenuOver(event) {
    cancelClose();

    const link = event.target instanceof Element ? event.target.closest('[data-megamenu-link]') : null;
    if (link instanceof HTMLAnchorElement) {
      activate(link);
    }
  }

  /**
   * @param {MouseEvent} event
   */
  function handleMegamenuLeave(event) {
    const relatedTarget = event.relatedTarget;
    if (relatedTarget instanceof Node && menu.contains(relatedTarget)) return;

    scheduleClose();
  }

  /**
   * @param {MouseEvent} event
   */
  function handleLeave(event) {
    const relatedTarget = event.relatedTarget;
    if (relatedTarget instanceof Node && header.contains(relatedTarget)) return;

    scheduleClose();
  }

  /**
   * Mobile menu list: clicking a link activates its megamenu (desktop parity,
   * but triggered by click instead of hover). Links without a matching
   * megamenu panel keep their default navigation.
   *
   * @param {MouseEvent} event
   */
  function handleMobileMenuClick(event) {
    const link = event.target instanceof Element ? event.target.closest('[data-megamenu-link]') : null;
    if (!(link instanceof HTMLAnchorElement)) return;

    const megamenuId = link.dataset.megamenuLink;
    const panel = megamenuId ? megamenuWrapper.querySelector(`[data-megamenu-id="${megamenuId}"]`) : null;
    if (!panel) return;

    event.preventDefault();
    activate(link);
  }

  /**
   * @param {MouseEvent} event
   */
  function toggleMobileMenu(event) {
    event.preventDefault();

    if (document.body.classList.contains('megamenu-active')) {
      closeMegamenu();
      return;
    }

    const link = getDefaultMenuLink();
    if (link) {
      activate(link);
    }
  }

  menu.addEventListener('mouseover', handleMenuOver);
  megamenuWrapper.addEventListener('mouseover', handleMegamenuOver);
  megamenuWrapper.addEventListener('mouseleave', handleMegamenuLeave);
  header.addEventListener('mouseleave', handleLeave);

  const overlay = document.querySelector('.menu-page-overlay');
  overlay?.addEventListener('click', closeMegamenu);

  const mobileMenuToggle = header.querySelector('.mobile-menu-icons [js-menu-toggle]');
  mobileMenuToggle?.addEventListener('click', toggleMobileMenu);

  const mobileMenuList = megamenuWrapper.querySelector('.mobile-menu__menu-list');
  mobileMenuList?.addEventListener('click', handleMobileMenuClick);

  const mobileMenuClose = megamenuWrapper.querySelector('.mobile-menu__header-close-icon');
  mobileMenuClose?.addEventListener('click', (event) => {
    event.preventDefault();
    closeMegamenu();
  });
}

const header = document.querySelector('#header-component');
if (header instanceof HTMLElement) {
  initMegamenu(header);
}
